import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';

/**
 * AuthCallback, handles Supabase OAuth redirect.
 *
 * Supabase sets the session via hash fragment + onAuthStateChange. We just
 * need to wait for the session to land, then route the user:
 *  - if their profile is missing required fields (country/currency/username),
 *    send them to /onboarding/complete
 *  - otherwise straight to /dashboard
 */
export default function AuthCallback() {
  usePageTitle('Signing in');
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // small delay for Supabase to process hash tokens
      await new Promise((r) => setTimeout(r, 300));
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user) {
        nav('/login', { replace: true });
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, country, currency, name')
        .eq('id', session.user.id)
        .maybeSingle();
      const needs = !profile || !profile.username || !profile.country || !profile.currency;
      nav(needs ? '/onboarding/complete' : '/dashboard', { replace: true });
    })();
    return () => { cancelled = true; };
  }, [nav]);

  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
      Signing you in&hellip;
    </div>
  );
}
